/**
 * NIVARO header behaviour.
 * - Desktop mega menus: hover intent, keyboard disclosure toggles, ArrowDown,
 *   Escape, outside click, theme-editor block select.
 * - Utility popovers (account): click toggle, Escape, outside click.
 * - Search: anchors Horizon's #search-modal dialog under the search icon.
 * - Mobile drawer (<dialog>): open/close with animation, scroll lock,
 *   drill-down panels, lazy predictive search panel.
 * - Mirrors Horizon's cart count into NIVARO's always-visible bronze bubbles.
 * - Mobile announcement rotation.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const DESKTOP = window.matchMedia('(min-width: 990px)');
const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

/* ----------------------------------------------------------- mega menus */
const megaTimers = new WeakMap();

function megaParts(item) {
  return {
    link: item.querySelector('[data-nv-mega-link]'),
    toggle: item.querySelector('[data-nv-mega-toggle]'),
    panel: item.querySelector('[data-nv-mega-panel]'),
  };
}

function openMega(item) {
  if (!item || item.classList.contains('is-open')) return;
  document.querySelectorAll('[data-nv-mega].is-open').forEach((other) => other !== item && closeMega(other));
  closePops();
  warmMegaImages();
  const { toggle, panel } = megaParts(item);
  item.classList.add('is-open');
  panel?.removeAttribute('hidden');
  toggle?.setAttribute('aria-expanded', 'true');
}

function closeMega(item, { focusToggle = false } = {}) {
  if (!item || !item.classList.contains('is-open')) return;
  const { toggle, panel } = megaParts(item);
  item.classList.remove('is-open');
  panel?.setAttribute('hidden', '');
  toggle?.setAttribute('aria-expanded', 'false');
  if (focusToggle) (toggle || megaParts(item).link)?.focus();
}

function closeAllMegas() {
  document.querySelectorAll('[data-nv-mega].is-open').forEach((item) => closeMega(item));
}

function schedule(item, fn, delay) {
  clearTimeout(megaTimers.get(item));
  megaTimers.set(item, setTimeout(fn, delay));
}

/** Warm up mega-menu images on first nav intent so panels open fully painted. */
function warmMegaImages() {
  document.querySelectorAll('.nv-mega img[loading="lazy"]').forEach((img) => {
    img.loading = 'eager';
  });
}

function initMegas(root = document) {
  const nav = root.querySelector('.nv-nav');
  if (nav && !nav.dataset.nvWarm) {
    nav.dataset.nvWarm = '1';
    nav.addEventListener('pointerenter', warmMegaImages, { once: true });
    nav.addEventListener('focusin', warmMegaImages, { once: true });
  }

  root.querySelectorAll('[data-nv-mega]').forEach((item) => {
    if (item.dataset.nvBound) return;
    item.dataset.nvBound = '1';
    const { link, toggle, panel } = megaParts(item);

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !DESKTOP.matches) return;
      const anyOpen = document.querySelector('[data-nv-mega].is-open');
      schedule(item, () => openMega(item), anyOpen ? 0 : 110);
    });
    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      schedule(item, () => closeMega(item), 180);
    });

    toggle?.addEventListener('click', () => {
      if (item.classList.contains('is-open')) closeMega(item);
      else {
        openMega(item);
        panel?.querySelector(FOCUSABLE)?.focus();
      }
    });

    link?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === ' ') {
        event.preventDefault();
        openMega(item);
        panel?.querySelector(FOCUSABLE)?.focus();
      }
    });

    // Touch / pen at desktop width (tablets): first tap opens, second follows.
    link?.addEventListener('click', (event) => {
      if (!DESKTOP.matches || HOVER.matches) return;
      if (!item.classList.contains('is-open')) {
        event.preventDefault();
        openMega(item);
      }
    });

    item.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && item.contains(next)) return;
      closeMega(item);
    });

    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && item.classList.contains('is-open')) {
        event.stopPropagation();
        closeMega(item, { focusToggle: true });
      }
    });
  });
}

/* --------------------------------------------------------------- popovers */
function popParts(wrap) {
  return { toggle: wrap.querySelector('[data-nv-pop-toggle]'), panel: wrap.querySelector('[data-nv-pop-panel]') };
}

function openPop(wrap) {
  closeAllMegas();
  closePops(wrap);
  const { toggle, panel } = popParts(wrap);
  const bar = document.querySelector('.nv-header__bar');
  if (bar) document.documentElement.style.setProperty('--nv-pop-top', `${Math.round(bar.getBoundingClientRect().bottom + 8)}px`);
  panel?.removeAttribute('hidden');
  toggle?.setAttribute('aria-expanded', 'true');
}

function closePop(wrap, { focusToggle = false } = {}) {
  const { toggle, panel } = popParts(wrap);
  if (!panel || panel.hasAttribute('hidden')) return;
  panel.setAttribute('hidden', '');
  toggle?.setAttribute('aria-expanded', 'false');
  if (focusToggle) toggle?.focus();
}

function closePops(except) {
  document.querySelectorAll('[data-nv-pop]').forEach((wrap) => wrap !== except && closePop(wrap));
}

function initPops(root = document) {
  root.querySelectorAll('[data-nv-pop]').forEach((wrap) => {
    if (wrap.dataset.nvBound) return;
    wrap.dataset.nvBound = '1';
    const { toggle, panel } = popParts(wrap);
    toggle?.addEventListener('click', () => {
      if (panel?.hasAttribute('hidden')) openPop(wrap);
      else closePop(wrap);
    });
    wrap.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closePop(wrap, { focusToggle: true });
      }
    });
    wrap.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && wrap.contains(next)) return;
      if (next) closePop(wrap);
    });
  });
}

document.addEventListener('pointerdown', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  if (!target.closest('[data-nv-mega]')) closeAllMegas();
  if (!target.closest('[data-nv-pop]')) closePops();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  closeAllMegas();
  closePops();
});

/* ------------------------------------------------------------------ search */
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-nv-search-open]') : null;
    if (!button) return;
    closeAllMegas();
    closePops();
    const dialog = document.querySelector('#search-modal dialog');
    const bar = document.querySelector('.nv-header__bar');
    if (!dialog || !bar) return;
    const barRect = bar.getBoundingClientRect();
    const actions = button.closest('.nv-header__actions') || button;
    const top = Math.max(12, Math.round(barRect.bottom + 8));
    const right = Math.max(16, Math.round(window.innerWidth - actions.getBoundingClientRect().right));
    dialog.style.setProperty('--nv-search-top', `${top}px`);
    dialog.style.setProperty('--nv-search-right', `${right}px`);
  },
  true
);

/* ------------------------------------------------------------------ drawer */
function initDrawer() {
  const drawer = document.querySelector('[data-nv-drawer]');
  if (!(drawer instanceof HTMLDialogElement) || drawer.dataset.nvBound) return;
  drawer.dataset.nvBound = '1';
  const openers = document.querySelectorAll('[data-nv-drawer-open]');
  let lastOpener = null;

  const showPanel = (name, back = false) => {
    const target = drawer.querySelector(`[data-nv-panel="${CSS.escape(name)}"]`);
    if (!target) return;
    drawer.querySelectorAll('[data-nv-panel]').forEach((panel) => {
      const active = panel === target;
      panel.toggleAttribute('hidden', !active);
      panel.classList.toggle('is-active', active);
      panel.classList.remove('is-entering', 'is-entering-back');
    });
    drawer.querySelectorAll('[data-nv-drawer-go]').forEach((btn) => {
      btn.setAttribute('aria-expanded', btn.getAttribute('data-nv-drawer-go') === name ? 'true' : 'false');
    });
    if (!REDUCED.matches) {
      void target.offsetWidth;
      target.classList.add(back ? 'is-entering-back' : 'is-entering');
    }
    target.scrollTop = 0;

    if (name === 'search') mountDrawerSearch(target);
    const focusTarget =
      name === 'search'
        ? target.querySelector('input[type="search"]')
        : target.querySelector('[data-nv-drawer-back]') || target.querySelector(FOCUSABLE);
    focusTarget?.focus({ preventScroll: true });
  };

  const open = (panel = 'main', opener = null) => {
    lastOpener = opener;
    closeAllMegas();
    closePops();
    if (!drawer.open) {
      drawer.classList.remove('is-closing');
      drawer.showModal();
      lockScroll(drawer);
      document.documentElement.classList.add('nv-lock');
      openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    }
    showPanel(panel);
    if (panel === 'main') drawer.querySelector('[data-nv-drawer-close]')?.focus({ preventScroll: true });
  };

  const finishClose = () => {
    drawer.classList.remove('is-closing');
    if (drawer.open) drawer.close();
    unlockScroll(drawer);
    document.documentElement.classList.remove('nv-lock');
    openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    showPanel('main');
  };

  const close = ({ restoreFocus = true } = {}) => {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;
    if (REDUCED.matches) {
      finishClose();
    } else {
      drawer.classList.add('is-closing');
      setTimeout(finishClose, 230);
    }
    if (restoreFocus) lastOpener?.focus({ preventScroll: true });
  };

  openers.forEach((btn) => btn.addEventListener('click', () => open('main', btn)));

  drawer.addEventListener('cancel', (event) => {
    event.preventDefault();
    const active = drawer.querySelector('[data-nv-panel].is-active');
    if (active && active.getAttribute('data-nv-panel') !== 'main') showPanel('main', true);
    else close();
  });

  drawer.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target === drawer) {
      const rect = drawer.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) close();
      return;
    }
    if (target.closest('[data-nv-drawer-close]')) return close();
    const go = target.closest('[data-nv-drawer-go]');
    if (go) return showPanel(go.getAttribute('data-nv-drawer-go'));
    if (target.closest('[data-nv-drawer-back]')) return showPanel('main', true);
    if (target.closest('[data-nv-drawer-dismiss]')) return close({ restoreFocus: false });
    if (target.closest('a[href]')) {
      // Same-page anchors and new tabs keep the drawer usable.
      const link = target.closest('a[href]');
      if (link.target !== '_blank') setTimeout(() => close({ restoreFocus: false }), 0);
    }
  });

  DESKTOP.addEventListener('change', (event) => event.matches && drawer.open && finishClose());

  window.NV = Object.assign(window.NV || {}, { menu: { open, close, showPanel } });
}

function mountDrawerSearch(panel) {
  const slot = panel.querySelector('[data-nv-drawer-search-slot]');
  const template = panel.querySelector('template[data-nv-drawer-search-template]');
  if (!slot || !template || slot.childElementCount) return;
  slot.append(template.content.cloneNode(true));
}

/* -------------------------------------------------------------- cart count */
function syncCartCount() {
  const source = document.querySelector('.nv-header [ref="cartBubbleCount"]');
  if (!source) return;
  const render = () => {
    const text = source.textContent.trim();
    const value = source.classList.contains('hidden') || text === '' ? (text === '' && !source.classList.contains('hidden') ? '99+' : '0') : text;
    document.querySelectorAll('[data-nv-cart-count]').forEach((el) => {
      if (el.textContent !== value) el.textContent = value;
    });
  };
  new MutationObserver(render).observe(source, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  render();
}

/* ------------------------------------------------------------ announcement */
function initAnnouncements() {
  document.querySelectorAll('[data-nv-announce]').forEach((bar) => {
    if (bar.dataset.nvBound) return;
    bar.dataset.nvBound = '1';
    const items = Array.from(bar.querySelectorAll('.nv-announce__item'));
    if (items.length < 2) return;
    const speed = Math.max(2000, Number(bar.dataset.nvAnnounceSpeed) || 4000);
    let index = 0;
    let paused = false;
    bar.addEventListener('pointerenter', () => (paused = true));
    bar.addEventListener('pointerleave', () => (paused = false));
    bar.addEventListener('focusin', () => (paused = true));
    bar.addEventListener('focusout', () => (paused = false));
    setInterval(() => {
      if (paused || DESKTOP.matches || document.hidden) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, speed);
  });
}

/* ----------------------------------------------------------- theme editor */
document.addEventListener('shopify:block:select', (event) => {
  const item = document.querySelector(`[data-nv-mega][data-block-id="${event.detail?.blockId}"]`);
  if (item && DESKTOP.matches) openMega(item);
});
document.addEventListener('shopify:block:deselect', () => closeAllMegas());
document.addEventListener('shopify:section:load', () => init());

function init() {
  initMegas();
  initPops();
  initDrawer();
  syncCartCount();
  initAnnouncements();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

window.NV = Object.assign(window.NV || {}, {
  mega: {
    open: (id) => openMega(document.querySelector(`[data-nv-mega][data-block-id="${id}"]`) || document.querySelectorAll('[data-nv-mega]')[Number(id) || 0]),
    close: closeAllMegas,
  },
});
